import { Wine, WineColor } from '../types';
import { getWineDisplayName, hasDistinctName } from '../utils';

interface WineListProps {
  wines: Wine[];
  onSelectWine: (wine: Wine) => void;
}

export function WineList({ wines, onSelectWine }: WineListProps) {
  const getWineColorClass = (color?: WineColor) => {
    switch (color) {
      case 'red': return 'wine-color-red';
      case 'white': return 'wine-color-white';
      case 'rosé': return 'wine-color-rose';
      case 'orange': return 'wine-color-orange';
      default: return 'wine-color-red';
    }
  };

  if (wines.length === 0) {
    return (
      <div className="ios-card" style={{ padding: '32px 16px', textAlign: 'center' }}>
        <div style={{ fontSize: '48px', marginBottom: '12px' }}>🍷</div>
        <p className="wine-title" style={{ 
          fontSize: '18px', 
          color: 'var(--color-text)',
          marginBottom: '4px'
        }}>
          No wines found
        </p>
        <p style={{ fontSize: '14px', color: 'var(--color-text-muted)' }}>
          Add your first bottle or adjust your filters
        </p>
      </div>
    );
  }

  return (
    <div className="ios-card" style={{ overflow: 'hidden' }}>
      {wines.map((wine, index) => {
        const displayName = getWineDisplayName(wine);
        const showProducerLine = hasDistinctName(wine);
        
        return (
          <div
            key={wine.id}
            className="ios-list-row"
            onClick={() => onSelectWine(wine)}
            style={{ 
              cursor: 'pointer',
              borderBottom: index < wines.length - 1 ? '1px solid var(--color-border)' : 'none'
            }}
          >
            {/* Color bar */}
            <div 
              className={`wine-color-bar ${getWineColorClass(wine.color)}`}
              style={{ height: '44px' }}
            />
            
            {/* Wine info */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
                <span 
                  className="wine-title" 
                  style={{ 
                    fontSize: '16px', 
                    color: 'var(--color-text)',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}
                >
                  {displayName}
                </span>
                {wine.vintage && (
                  <span style={{ 
                    fontSize: '14px', 
                    color: 'var(--color-text-muted)',
                    flexShrink: 0
                  }}>
                    {wine.vintage}
                  </span>
                )}
              </div>
              
              {showProducerLine && (
                <div style={{ 
                  fontSize: '13px', 
                  color: 'var(--color-text-muted)',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}>
                  {wine.producer}
                  {wine.varietal && ` · ${wine.varietal}`}
                </div>
              )}
              
              {!showProducerLine && wine.region && (
                <div style={{ 
                  fontSize: '13px', 
                  color: 'var(--color-text-muted)',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}>
                  {wine.region}{wine.country && `, ${wine.country}`}
                </div>
              )}
              
              {/* Ratings line */}
              {(wine.my_rating || (wine.critic_scores && wine.critic_scores.length > 0)) && (
                <div style={{ 
                  display: 'flex', 
                  flexWrap: 'wrap', 
                  gap: '8px', 
                  marginTop: '4px',
                  fontSize: '12px'
                }}>
                  {wine.my_rating && (
                    <span style={{ fontWeight: '600', color: 'var(--color-gold)' }}>
                      {wine.my_rating}
                    </span>
                  )}
                  {wine.critic_scores?.find(s => s.critic_name === 'Vivino') && (
                    <span style={{ color: '#9b2335' }}>
                      <span style={{ fontWeight: '600' }}>
                        {wine.critic_scores.find(s => s.critic_name === 'Vivino')?.score.toFixed(1)}
                      </span>
                      {' ★'}
                    </span>
                  )}
                  {wine.critic_scores?.filter(s => s.critic_name !== 'Vivino').slice(0, 1).map(score => (
                    <span key={score.id} style={{ color: '#1e40af' }}>
                      <span style={{ fontWeight: '600' }}>{score.score}</span>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Right side: quantity badge + chevron */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
              <div style={{ 
                padding: '4px 10px', 
                borderRadius: '12px', 
                fontSize: '13px', 
                fontWeight: '600',
                background: 'var(--color-parchment-dark)', 
                color: 'var(--color-text)' 
              }}>
                {wine.quantity}
              </div>
              <span style={{ fontSize: '18px', color: 'var(--color-border)' }}>›</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}
